// views/Dashboard/Profile/components/CasosListView.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Button,
  Spinner,
  Center,
  useToast,
  Icon,
  IconButton,
  SimpleGrid,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal
} from '@chakra-ui/react';
import {
  Plus,
  FileText,
  Clock,
  MoreVertical,
  Eye,
  Trash2,
  Play,
  Pause,
  CheckCircle
} from 'lucide-react';
import { FiInbox } from 'react-icons/fi';
import { casosService, casosUtils } from 'services/casosService';

const STATUS_STYLE = {
  abierto: { bg: 'mint.bg', ink: 'mint.ink' },
  pausado: { bg: 'butter.bg', ink: 'butter.ink' },
  cerrado: { bg: 'cream.bg2', ink: 'ink.400' },
};

const STAGE_PASTEL = {
  recepcion: 'sky',
  investigacion: 'lav',
  resolucion: 'butter',
  cierre: 'mint',
};

const CasosListView = ({ customer, onSelectCaso, onCreateCaso }) => {
  // Estados
  const [casos, setCasos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const toast = useToast();

  // Cargar casos del usuario
  const loadCasos = useCallback(async () => {
    if (!customer?.username) {
      setError('Username no disponible');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await casosService.getCasosByUser(customer.username);

      if (response.success) {
        setCasos(response.data || []);
      } else {
        throw new Error(response.message || 'Error al cargar casos');
      }

    } catch (err) {
      setError(err.message || 'Error al cargar casos');
      setCasos([]);
    } finally {
      setLoading(false);
    }
  }, [customer?.username]);

  // Cargar casos al montar el componente
  useEffect(() => {
    loadCasos();
  }, [loadCasos]);

  // Manejar cambio de status de caso
  const handleStatusChange = async (casoId, action) => {
    try {
      const response = await casosService.changeCasoStatus(casoId, action, null, null);

      if (response.success) {
        toast({
          title: 'Status actualizado',
          description: response.message,
          status: 'success',
          duration: 3000,
          isClosable: true
        });

        loadCasos();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Error al cambiar status del caso',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  // Manejar eliminación de caso
  const handleDeleteCaso = async (casoId, casoTitle) => {
    const confirmed = window.confirm(
      `¿Estás seguro de que deseas eliminar el caso "${casoTitle}"?\n\nEsta acción no se puede deshacer.`
    );

    if (!confirmed) return;

    try {
      const response = await casosService.deleteCaso(casoId);

      if (response.success) {
        toast({
          title: 'Caso eliminado',
          description: response.message,
          status: 'success',
          duration: 3000,
          isClosable: true
        });

        loadCasos();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Error al eliminar caso',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  // Renderizar tarjeta de caso
  const renderCasoCard = (caso) => {
    const statusStyle = STATUS_STYLE[caso.status] || STATUS_STYLE.cerrado;
    const stagePastel = STAGE_PASTEL[caso.current_stage] || 'sky';

    return (
      <Box
        key={caso.id}
        bg="white"
        border="1px solid" borderColor="cream.line"
        borderRadius="16px"
        cursor="pointer"
        transition="background .13s, border-color .13s"
        _hover={{ bg: 'coral.tint', borderColor: 'coral.soft' }}
        onClick={() => onSelectCaso && onSelectCaso(caso)}
        p={4}
      >
        <VStack align="stretch" spacing={3}>
          {/* Header con título y menú de acciones */}
          <HStack justify="space-between" align="start">
            <VStack align="start" spacing={1} flex="1" minW={0}>
              <Text fontSize="14px" fontWeight="800" color="ink.900" noOfLines={2}>
                {caso.title}
              </Text>
              {caso.description && (
                <Text fontSize="12px" color="ink.400" noOfLines={2}>
                  {caso.description}
                </Text>
              )}
            </VStack>

            {/* Menú de acciones */}
            <Menu>
              <MenuButton
                as={IconButton}
                icon={<Icon as={MoreVertical} boxSize="16px" />}
                size="sm"
                variant="ghost"
                color="ink.400"
                _hover={{ bg: 'cream.bg2', color: 'coral.500' }}
                onClick={(e) => e.stopPropagation()}
              />
              <Portal>
                <MenuList zIndex={99999} fontFamily="jakarta" borderRadius="12px">
                  <MenuItem
                    icon={<Icon as={Eye} boxSize="14px" />}
                    fontSize="13.5px"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectCaso && onSelectCaso(caso);
                    }}
                  >
                    Ver detalles
                  </MenuItem>

                  {caso.status === 'abierto' && (
                    <MenuItem
                      icon={<Icon as={Pause} boxSize="14px" />}
                      fontSize="13.5px"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusChange(caso.id, 'pause');
                      }}
                    >
                      Pausar caso
                    </MenuItem>
                  )}

                  {caso.status === 'pausado' && (
                    <MenuItem
                      icon={<Icon as={Play} boxSize="14px" />}
                      fontSize="13.5px"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusChange(caso.id, 'resume');
                      }}
                    >
                      Reanudar caso
                    </MenuItem>
                  )}

                  {caso.status === 'abierto' && (
                    <MenuItem
                      icon={<Icon as={CheckCircle} boxSize="14px" />}
                      fontSize="13.5px"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusChange(caso.id, 'close');
                      }}
                    >
                      Cerrar caso
                    </MenuItem>
                  )}

                  <MenuItem
                    icon={<Icon as={Trash2} boxSize="14px" />}
                    color="#DC2626"
                    fontSize="13.5px"
                    _hover={{ bg: 'rose.bg' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteCaso(caso.id, caso.title);
                    }}
                  >
                    Eliminar caso
                  </MenuItem>
                </MenuList>
              </Portal>
            </Menu>
          </HStack>

          <Divider borderColor="cream.line" />

          {/* Información del caso */}
          <HStack justify="space-between" wrap="wrap" spacing={2}>
            {/* Stage actual */}
            <VStack align="start" spacing={1}>
              <Text fontSize="10px" color="ink.400" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em">
                Etapa actual
              </Text>
              <Box as="span" px="9px" py="2px" borderRadius="999px" bg={`${stagePastel}.bg`} color={`${stagePastel}.ink`}
                fontSize="11px" fontWeight="700">
                {caso.stage_name || casosUtils.getStageName(caso.current_stage)}
              </Box>
            </VStack>

            {/* Status */}
            <VStack align="start" spacing={1}>
              <Text fontSize="10px" color="ink.400" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em">
                Estado
              </Text>
              <Box as="span" px="9px" py="2px" borderRadius="999px" bg={statusStyle.bg} color={statusStyle.ink}
                fontSize="11px" fontWeight="700">
                {caso.status_name || casosUtils.getStatusName(caso.status)}
              </Box>
            </VStack>

            {/* Número de fichas */}
            <VStack align="start" spacing={1}>
              <Text fontSize="10px" color="ink.400" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em">
                Fichas
              </Text>
              <HStack spacing={1}>
                <Icon as={FileText} boxSize={3} color="ink.700" />
                <Text fontSize="13px" fontWeight="700" color="ink.900">
                  {caso.fichas_count || 0}
                </Text>
              </HStack>
            </VStack>
          </HStack>

          {/* Fecha de creación */}
          <HStack spacing={2} color="ink.400">
            <Icon as={Clock} boxSize={3} />
            <Text fontSize="11.5px">
              Creado {casosUtils.getTimeAgo(caso.created_at)}
            </Text>
            {caso.created_by && (
              <>
                <Text fontSize="11.5px">por</Text>
                <Text fontSize="11.5px" fontWeight="700">
                  {caso.created_by}
                </Text>
              </>
            )}
          </HStack>
        </VStack>
      </Box>
    );
  };

  // Estados de carga
  if (loading) {
    return (
      <Center py={8} fontFamily="jakarta">
        <VStack>
          <Spinner size="lg" color="coral.500" />
          <Text color="ink.400" fontSize="13.5px">Cargando casos...</Text>
        </VStack>
      </Center>
    );
  }

  // Estado de error
  if (error) {
    return (
      <Box bg="rose.bg" borderRadius="14px" p={4} fontFamily="jakarta">
        <VStack align="start" spacing={1}>
          <Text fontWeight="700" fontSize="13.5px" color="rose.ink">Error al cargar casos</Text>
          <Text fontSize="12.5px" color="rose.ink">{error}</Text>
        </VStack>
      </Box>
    );
  }

  // Sin casos
  if (casos.length === 0) {
    return (
      <VStack
        py={12} px={6}
        bg="cream.bg2" borderRadius="16px" border="1.5px dashed" borderColor="cream.line2"
        spacing={4} fontFamily="jakarta"
      >
        <Icon as={FiInbox} boxSize={9} color="ink.400" />
        <VStack spacing={2}>
          <Text fontSize="14px" fontWeight="800" color="ink.900">
            No hay casos para este usuario
          </Text>
          <Text fontSize="12.5px" color="ink.400" textAlign="center" maxW="400px">
            Los casos te permiten organizar y dar seguimiento a múltiples formularios
            relacionados con un tema específico.
          </Text>
        </VStack>

        {onCreateCaso && (
          <Button
            size="md" borderRadius="11px" fontWeight="700"
            bg="coral.500" color="#fff" _hover={{ bg: 'coral.600' }}
            leftIcon={<Icon as={Plus} boxSize="15px" />}
            onClick={onCreateCaso}
          >
            Crear primer caso
          </Button>
        )}
      </VStack>
    );
  }

  // Renderizar lista de casos
  return (
    <VStack align="stretch" spacing={5} fontFamily="jakarta">
      {/* Header con estadísticas */}
      <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={2}>
          <Flex w="34px" h="34px" borderRadius="10px" bg="coral.soft" align="center" justify="center">
            <Icon as={FileText} color="coral.700" boxSize={4} />
          </Flex>
          <VStack align="start" spacing={0}>
            <Text fontSize="15px" fontWeight="800" color="ink.900">
              Casos de {customer?.first_name} {customer?.last_name}
            </Text>
            <Text fontSize="12px" color="ink.400" fontWeight="500">
              {casos.length} caso{casos.length !== 1 ? 's' : ''} encontrado{casos.length !== 1 ? 's' : ''}
            </Text>
          </VStack>
        </HStack>

        {onCreateCaso && (
          <Button
            size="sm" borderRadius="9px" fontWeight="700" fontSize="12.5px"
            bg="coral.500" color="#fff" _hover={{ bg: 'coral.600' }}
            leftIcon={<Icon as={Plus} boxSize="14px" />}
            onClick={onCreateCaso}
          >
            Nuevo caso
          </Button>
        )}
      </Flex>

      {/* Grid de casos */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
        {casos.map(renderCasoCard)}
      </SimpleGrid>

      {/* Información adicional */}
      <Box bg="sky.bg" borderRadius="12px" p={3}>
        <HStack spacing={2}>
          <Icon as={FileText} color="sky.ink" boxSize={4} />
          <Text fontSize="12.5px" color="sky.ink">
            <strong>Tip:</strong> Haz clic en cualquier caso para ver sus fichas y gestionar el flujo de trabajo.
          </Text>
        </HStack>
      </Box>
    </VStack>
  );
};

export default CasosListView;
